import {
  buildWorkspaceChips,
  buildWorkspaceSidebarItems,
  normalizeWorkspaceRoute,
} from './workspaceRouteModel';
import {
  WORKSPACE_CONTROL_PRESETS,
  WORKSPACE_PANEL_KINDS,
  hasWorkspaceRegistryEntry,
} from './workspaceRegistry';
import {
  normalizeSkeletonSpec,
  validateSkeletonSpec,
} from '../../skeletons/skeletonSpec';

export const WORKSPACE_DESCRIPTOR_VERSION = 1;

const isPlainObject = (value) => (
  value != null
  && typeof value === 'object'
  && !Array.isArray(value)
);

const isNonEmptyString = (value) => (
  typeof value === 'string' && value.trim().length > 0
);

const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeId = (value) => (
  isNonEmptyString(value) ? value.trim() : null
);

const normalizeControlRef = (control) => {
  if (isNonEmptyString(control)) {
    const id = normalizeId(control);
    return { id, preset: id };
  }

  if (!isPlainObject(control)) return null;

  const id = normalizeId(control.id || control.preset || control.type || control.kind);
  if (!id) return null;

  return {
    ...control,
    id,
    preset: normalizeId(control.preset || control.type || control.kind || id),
  };
};

const normalizePanelRef = (panel) => {
  if (isNonEmptyString(panel)) {
    const id = normalizeId(panel);
    return { id, kind: id };
  }

  if (!isPlainObject(panel)) return null;

  const id = normalizeId(panel.id || panel.kind || panel.type);
  if (!id) return null;

  return {
    ...panel,
    id,
    kind: normalizeId(panel.kind || panel.type || id),
    skeleton: normalizeSkeletonSpec(panel.skeleton),
  };
};

const normalizeVisualizationRef = (visualization) => {
  if (isNonEmptyString(visualization)) {
    const id = normalizeId(visualization);
    return { id, kind: id };
  }

  if (!isPlainObject(visualization)) return null;

  const id = normalizeId(visualization.id || visualization.kind || visualization.type);
  if (!id) return null;

  return {
    ...visualization,
    id,
    kind: normalizeId(visualization.kind || visualization.type || id),
  };
};

const normalizeColumnRef = (column) => {
  if (isNonEmptyString(column)) {
    const id = normalizeId(column);
    return { id, preset: id };
  }

  if (!isPlainObject(column)) return null;

  const id = normalizeId(column.id || column.preset || column.kind || column.type || column.field);
  if (!id) return null;

  return {
    ...column,
    id,
    preset: normalizeId(column.preset || column.kind || column.type || id),
  };
};

const inferPanelsFromLayout = (layout) => {
  if (layout === WORKSPACE_PANEL_KINDS.CHART_TABLE) {
    return [
      { id: WORKSPACE_PANEL_KINDS.CHART, kind: WORKSPACE_PANEL_KINDS.CHART },
      { id: WORKSPACE_PANEL_KINDS.TABLE, kind: WORKSPACE_PANEL_KINDS.TABLE },
    ];
  }

  if (layout === WORKSPACE_PANEL_KINDS.CHART) {
    return [{ id: WORKSPACE_PANEL_KINDS.CHART, kind: WORKSPACE_PANEL_KINDS.CHART }];
  }

  if (layout === WORKSPACE_PANEL_KINDS.TABLE) {
    return [{ id: WORKSPACE_PANEL_KINDS.TABLE, kind: WORKSPACE_PANEL_KINDS.TABLE }];
  }

  return [];
};

const normalizeWorkspaceViewDescriptor = (view) => {
  if (!isPlainObject(view) || view.visible === false) return null;

  const id = normalizeId(view.id);
  const label = isNonEmptyString(view.label) ? view.label.trim() : null;
  if (!id || !label) return null;

  const controls = [
    ...asArray(view.defaultControls),
    ...asArray(view.controls),
  ]
    .map(normalizeControlRef)
    .filter(Boolean);
  const panels = (
    asArray(view.panels).map(normalizePanelRef).filter(Boolean)
  );
  const normalizedPanels = panels.length > 0
    ? panels
    : inferPanelsFromLayout(view.layout).map(normalizePanelRef).filter(Boolean);

  return {
    ...view,
    id,
    label,
    layout: normalizeId(view.layout || view.viewLayout || WORKSPACE_PANEL_KINDS.SINGLE),
    viewLayout: normalizeId(view.viewLayout || view.layout || WORKSPACE_PANEL_KINDS.SINGLE),
    requiredData: normalizeId(view.requiredData || view.requiresData),
    controls,
    panels: normalizedPanels,
    skeleton: normalizeSkeletonSpec(view.skeleton),
    visualizations: asArray(view.visualizations).map(normalizeVisualizationRef).filter(Boolean),
    tableColumns: asArray(view.tableColumns).map(normalizeColumnRef).filter(Boolean),
  };
};

const normalizeWorkspaceSectionDescriptor = (section) => {
  if (!isPlainObject(section) || section.visible === false) return null;

  const id = normalizeId(section.id);
  const label = isNonEmptyString(section.label) ? section.label.trim() : null;
  if (!id || !label) return null;

  const views = asArray(section.views)
    .map(normalizeWorkspaceViewDescriptor)
    .filter(Boolean);

  if (views.length === 0) return null;

  return {
    ...section,
    id,
    label,
    defaultViewId: normalizeId(section.defaultViewId),
    views,
  };
};

export const normalizeWorkspaceDescriptor = (descriptor = {}) => {
  const sections = asArray(descriptor.sections)
    .map(normalizeWorkspaceSectionDescriptor)
    .filter(Boolean);
  const firstSection = sections[0] || null;
  const requestedDefaultSectionId = normalizeId(descriptor.defaultSectionId);
  const defaultSection = (
    sections.find((section) => section.id === requestedDefaultSectionId)
    || firstSection
  );
  const requestedDefaultViewId = normalizeId(
    defaultSection?.defaultViewId || descriptor.defaultViewId
  );
  const defaultView = defaultSection
    ? (
      defaultSection.views.find((view) => view.id === requestedDefaultViewId)
      || defaultSection.views[0]
    )
    : null;

  return {
    ...descriptor,
    version: descriptor.version || WORKSPACE_DESCRIPTOR_VERSION,
    id: normalizeId(descriptor.id),
    title: isNonEmptyString(descriptor.title) ? descriptor.title.trim() : '',
    description: isNonEmptyString(descriptor.description) ? descriptor.description.trim() : '',
    defaultSectionId: defaultSection?.id || null,
    defaultViewId: defaultView?.id || null,
    sections,
  };
};

const pushUnknownRegistryErrors = ({
  errors,
  descriptor,
  section,
  view,
  registryName,
  collectionName,
  idKey,
}) => {
  asArray(view[collectionName]).forEach((item) => {
    const registryId = item[idKey] || item.id;
    if (!hasWorkspaceRegistryEntry(registryName, registryId)) {
      errors.push(`${descriptor.id || 'workspace'}:${section.id}:${view.id} references unknown ${collectionName} preset "${registryId}"`);
    }
  });
};

export const validateWorkspaceDescriptor = (descriptor = {}, options = {}) => {
  const normalized = normalizeWorkspaceDescriptor(descriptor);
  const errors = [];
  const warnings = [];

  if (!normalized.id) errors.push('workspace descriptor requires an id');
  if (!normalized.title) errors.push(`${normalized.id || 'workspace'} requires a title`);
  if (normalized.sections.length === 0) {
    errors.push(`${normalized.id || 'workspace'} requires at least one visible section with one visible view`);
  }

  normalized.sections.forEach((section) => {
    section.views.forEach((view) => {
      if (options.requireComponent && !view.component && !view.render) {
        errors.push(`${normalized.id}:${section.id}:${view.id} requires a component or render function`);
      }

      pushUnknownRegistryErrors({
        errors,
        descriptor: normalized,
        section,
        view,
        registryName: 'controls',
        collectionName: 'controls',
        idKey: 'preset',
      });
      pushUnknownRegistryErrors({
        errors,
        descriptor: normalized,
        section,
        view,
        registryName: 'panels',
        collectionName: 'panels',
        idKey: 'kind',
      });
      pushUnknownRegistryErrors({
        errors,
        descriptor: normalized,
        section,
        view,
        registryName: 'visualizations',
        collectionName: 'visualizations',
        idKey: 'kind',
      });
      pushUnknownRegistryErrors({
        errors,
        descriptor: normalized,
        section,
        view,
        registryName: 'tableColumns',
        collectionName: 'tableColumns',
        idKey: 'preset',
      });

      if (view.skeleton) {
        const result = validateSkeletonSpec(view.skeleton);
        errors.push(...result.errors.map((error) => (
          `${normalized.id}:${section.id}:${view.id} skeleton ${error}`
        )));
      }

      view.panels.forEach((panel) => {
        if (!panel.skeleton) return;
        const result = validateSkeletonSpec(panel.skeleton);
        errors.push(...result.errors.map((error) => (
          `${normalized.id}:${section.id}:${view.id}:${panel.id} skeleton ${error}`
        )));
      });

      if (view.controls.length === 0) {
        warnings.push(`${normalized.id}:${section.id}:${view.id} declares no controls`);
      }
      if (view.panels.length === 0) {
        warnings.push(`${normalized.id}:${section.id}:${view.id} declares no panels`);
      }
    });
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    descriptor: normalized,
  };
};

export const normalizeWorkspaceDescriptorRoute = ({
  descriptor,
  sectionId,
  viewId,
} = {}) => {
  const normalized = normalizeWorkspaceDescriptor(descriptor);

  return normalizeWorkspaceRoute({
    sections: normalized.sections,
    defaultSectionId: normalized.defaultSectionId,
    defaultViewId: normalized.defaultViewId,
    sectionId,
    viewId,
  });
};

export const resolveWorkspaceViewLayout = (view = {}, fallback = WORKSPACE_PANEL_KINDS.SINGLE) => {
  const layout = normalizeId(view.viewLayout || view.layout || fallback) || fallback;

  if (layout === WORKSPACE_PANEL_KINDS.SPLIT || layout === WORKSPACE_PANEL_KINDS.CHART_TABLE) {
    return WORKSPACE_PANEL_KINDS.SPLIT;
  }

  if (layout === WORKSPACE_PANEL_KINDS.KPI_STRIP) {
    return WORKSPACE_PANEL_KINDS.SINGLE;
  }

  return layout;
};

export const resolveWorkspacePanel = (view = {}, panelIdOrKind) => {
  const panels = asArray(view.panels);
  if (panels.length === 0) return null;
  if (!panelIdOrKind) return panels[0];

  return panels.find((panel) => (
    panel.id === panelIdOrKind || panel.kind === panelIdOrKind
  )) || null;
};

export const buildWorkspaceFilterSummaryLabels = (view = {}, overrides = {}) => {
  if (Array.isArray(overrides.summaryLabels)) return overrides.summaryLabels;

  return asArray(view.controls)
    .map((control) => (
      control.sublabel
      || control.label
      || WORKSPACE_CONTROL_PRESETS[control.preset]?.label
      || control.id
    ))
    .filter(Boolean);
};

export const buildWorkspacePanelSkeleton = (view = {}, panelIdOrKind) => (
  resolveWorkspacePanel(view, panelIdOrKind)?.skeleton || view.skeleton || null
);

export const buildWorkspaceDescriptorSidebarItems = ({
  descriptor,
  buildPath,
  routeState,
} = {}) => {
  const normalized = normalizeWorkspaceDescriptor(descriptor);

  return buildWorkspaceSidebarItems({
    sections: normalized.sections,
    buildPath,
    routeState,
  });
};

export const buildWorkspaceDescriptorChips = ({
  descriptor,
  sectionId,
  viewId,
  routeState,
  extraChips,
} = {}) => {
  const route = normalizeWorkspaceDescriptorRoute({ descriptor, sectionId, viewId });

  return buildWorkspaceChips({
    section: route.section,
    view: route.view,
    routeState,
    extraChips,
  });
};
